<template>
    <!-- Update Available Notification -->
    <Card v-if="activeNotification === 'update'"
        class="fixed bottom-4 right-4 z-50 max-w-sm shadow-lg !border border-gray-200 dark:border-slate-600 animate-fade-in">
        <template #content>
            <div class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                    <div class="w-8 h-8 bg-purple-500 dark:bg-purple-600 rounded-full flex items-center justify-center">
                        <i class="pi pi-refresh text-white text-sm"></i>
                    </div>
                </div>
                <div class="flex-1">
                    <h4 class="font-semibold text-gray-900 dark:text-slate-100 mb-1">
                        {{ $t("pwa.update.title") }}
                    </h4>
                    <p class="text-sm text-gray-600 dark:text-slate-400 mb-3">
                        {{ $t("pwa.update.message") }}
                    </p>
                    <div class="flex gap-2">
                        <Button @click="updateApp" :label="$t('pwa.update.install')" size="small" />
                        <Button @click="dismissUpdate" :label="$t('pwa.update.later')" severity="secondary" outlined
                            size="small" />
                    </div>
                </div>
                <Button @click="dismissUpdate" icon="pi pi-times" text rounded
                    class="ml-2 text-gray-400 hover:text-gray-600 dark:text-slate-400 dark:hover:text-slate-300"
                    size="small" />
            </div>
        </template>
    </Card>

    <!-- Install App Prompt -->
    <Card v-else-if="activeNotification === 'install'"
        class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 !bg-gradient-to-r !from-blue-500 !to-cyan-600 dark:!from-blue-600 dark:!to-cyan-700 text-white animate-fade-in">
        <template #content>
            <div class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                    <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <i class="pi pi-download text-white text-sm"></i>
                    </div>
                </div>
                <div class="flex-1">
                    <h4 class="font-semibold mb-1 text-white">
                        {{ $t("pwa.install.title") }}
                    </h4>
                    <p class="text-sm text-white/90 mb-3">
                        {{ $t("pwa.install.message") }}
                    </p>
                    <div class="flex gap-2">
                        <Button @click="installApp" :label="$t('pwa.install.install')" severity="contrast"
                            size="small" />
                        <Button @click="dismissInstall" :label="$t('pwa.install.later')" severity="secondary" outlined
                            size="small" />
                    </div>
                </div>
                <Button @click="dismissInstall" icon="pi pi-times" text rounded
                    class="ml-2 text-white/70 hover:text-white" size="small" />
            </div>
        </template>
    </Card>

    <!-- Safari Install Prompt -->
    <Card v-else-if="activeNotification === 'safari_install'"
        class="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 !bg-gradient-to-r !from-blue-500 !to-cyan-600 dark:!from-blue-600 dark:!to-cyan-700 text-white animate-fade-in">
        <template #content>
            <div class="flex items-start">
                <div class="flex-shrink-0 mr-3">
                    <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <i class="pi pi-mobile text-white text-sm"></i>
                    </div>
                </div>
                <div class="flex-1">
                    <h4 class="font-semibold mb-1 text-white">
                        {{ $t("pwa.safari.title") }}
                    </h4>
                    <p class="text-sm text-white/90 mb-3">
                        {{ $t("pwa.safari.message") }}
                    </p>
                    <div class="flex flex-col gap-2 mb-3">
                        <div class="flex items-center text-sm text-white/90">
                            <div class="flex items-center justify-center w-6 h-6 bg-white/20 rounded-full mr-2 text-xs">
                                1
                            </div>
                            <span class="mr-2">{{ $t("pwa.safari.step1") }}</span>
                            <div
                                class="flex items-center justify-center w-6 h-6 bg-white/30 rounded border border-white/40">
                                <i class="pi pi-share-alt text-xs"></i>
                            </div>
                        </div>
                        <div class="flex items-center text-sm text-white/90">
                            <div class="flex items-center justify-center w-6 h-6 bg-white/20 rounded-full mr-2 text-xs">
                                2
                            </div>
                            <span>{{ $t("pwa.safari.step2") }}</span>
                        </div>
                    </div>
                    <Button @click="dismissSafariInstall" :label="$t('pwa.safari.later')" severity="secondary" outlined
                        size="small" />
                </div>
                <Button @click="dismissSafariInstall" icon="pi pi-times" text rounded
                    class="ml-2 text-white/70 hover:text-white" size="small" />
            </div>
        </template>
    </Card>

    <!-- Offline Notification -->
    <div v-if="!isOnline"
        class="fixed top-0 left-0 right-0 z-50 bg-orange-500 dark:bg-orange-600 text-white p-2 text-center text-sm animate-slide-down">
        <i class="pi pi-wifi-slash mr-2"></i>
        {{ $t("pwa.offline.message") }}
    </div>

    <!-- Online Notification (temporary) -->
    <div v-if="showOnlineNotification"
        class="fixed top-0 left-0 right-0 z-50 bg-green-500 dark:bg-green-600 text-white p-2 text-center text-sm animate-slide-down">
        <i class="pi pi-wifi mr-2"></i>
        {{ $t("pwa.online.message") }}
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { usePWAUpdate } from "@/composables/pwa";
import { useNetworkStatus } from "@/composables/network-status";
import { useI18n } from "vue-i18n";
import Card from "primevue/card";
import Button from "primevue/button";

const { t } = useI18n();

// PWA functionality
const {
    showUpdatePrompt,
    isInstallable,
    updateApp,
    dismissUpdate,
    installApp,
    dismissInstall,
    isSafari,
    showSafariInstallPrompt,
    dismissSafariInstall,
} = usePWAUpdate();

// Network status
const { isOnline } = useNetworkStatus();

// Notification Priority
const activeNotification = computed(() => {
    if (showUpdatePrompt.value) {
        return "update";
    }
    if (isInstallable.value && !isSafari.value) {
        return "install";
    }
    if (showSafariInstallPrompt.value) {
        return "safari_install";
    }
    return null;
});

// Show temporary online notification
const showOnlineNotification = ref(false);
let onlineTimeout: number;

onMounted(() => {
    // Watch for network status changes
    let wasOffline = false;

    const checkNetworkStatus = () => {
        if (!navigator.onLine) {
            wasOffline = true;
        } else if (wasOffline && navigator.onLine) {
            // Show online notification briefly
            showOnlineNotification.value = true;
            clearTimeout(onlineTimeout);
            onlineTimeout = window.setTimeout(() => {
                showOnlineNotification.value = false;
                wasOffline = false;
            }, 3000);
        }
    };

    window.addEventListener("online", checkNetworkStatus);
    window.addEventListener("offline", checkNetworkStatus);
});
</script>

<style scoped>
.animate-fade-in {
    animation: fadeIn 0.3s ease-out;
}

.animate-slide-down {
    animation: slideDown 0.3s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(0.625rem);
        /* 10px */
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideDown {
    from {
        transform: translateY(-100%);
    }

    to {
        transform: translateY(0);
    }
}
</style>
